import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { api } from '../services/api';
import './Profile.css';

interface User {
  userId: number;
  userName: string;
  fullName?: string;
  email?: string;
  createdAt?: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await api.get(`/users/${userId}`);
      if (!response.ok) {
        // If single user endpoint doesn't work, try alternative
        const altResponse = await api.get(`/users`);
        if (!altResponse.ok) throw new Error('Failed to fetch user details');
        
        const users = await altResponse.json();
        const userData = Array.isArray(users) 
          ? users.find((u: User) => u.userId === parseInt(userId))
          : users;
        
        if (userData) {
          setUser(userData);
        } else {
          throw new Error('User not found');
        }
      } else {
        const data = await response.json();
        setUser(data);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    fetchUserDetails();
  }, [userId, navigate, fetchUserDetails]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <Navigation />
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <Navigation />
      <div className="profile-content">
        <h1 className="profile-title">Profile</h1>
        
        {error && <div className="error-message">{error}</div>}
        
        {user && (
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {user.userName.charAt(0).toUpperCase()}
              </div>
              <h2>{user.fullName || user.userName}</h2>
            </div>
            
            <div className="profile-details">
              <div className="profile-detail-item">
                <span className="detail-label">User ID</span>
                <span className="detail-value">{user.userId}</span>
              </div>
              
              <div className="profile-detail-item">
                <span className="detail-label">Username</span>
                <span className="detail-value">{user.userName}</span>
              </div>
              
              {user.fullName && (
                <div className="profile-detail-item">
                  <span className="detail-label">Full Name</span>
                  <span className="detail-value">{user.fullName}</span>
                </div>
              )}
              
              {user.email && (
                <div className="profile-detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{user.email}</span>
                </div>
              )}
              
              {user.createdAt && (
                <div className="profile-detail-item">
                  <span className="detail-label">Member Since</span>
                  <span className="detail-value">{formatDate(user.createdAt)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;


